import { FC, useEffect, useState } from 'react';

import fetcher from 'api/fetcher';
import useDefaultAmountForReplenishment from 'utils/balance/useDefaultAmountForReplenishment';
import { CENTS_IN_RUB, formatCost } from 'utils/clickme';
import { NON_BREAKING_SPACE } from 'utils/constants/common';
import { RequisitesFormName } from 'utils/constants/requisites';

import { ElementShownAnchor } from '@hh.ru/analytics-js';
import clickmePaymentModalButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/invoice_purchase/clickme_payment_modal_button_click';
import clickmePaymentModalChipButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/invoice_purchase/clickme_payment_modal_chip_button_click';
import clickmePaymentModalElementShown from '@hh.ru/analytics-js-events/build/clickme/employer/invoice_purchase/clickme_payment_modal_element_shown';
import clickmePaymentModalFormFieldTouch from '@hh.ru/analytics-js-events/build/clickme/employer/invoice_purchase/clickme_payment_modal_form_field_touch';
import {
    ActionBar,
    Banner,
    BottomSheet,
    Button,
    ChipsContainer,
    CustomChip,
    FormLabel,
    Link,
    Modal,
    NavBar,
    NumberInput,
    Text,
    useBreakpoint,
    VSpacing,
    VSpacingContainer,
} from '@hh.ru/magritte-ui';

import { formatToReactComponent } from 'bloko/common/trl';

import useTrls from 'hooks/translation/useTrls';

import { purchase } from 'components/common/BalanceReplenishmentModal/purchase';
import withErrorBoundary from 'components/common/ErrorBoundary/withErrorBoundary';
import { MagritteModalHeader } from 'components/common/MagritteModalHeader/MagritteModalHeader';
import { RequisitesData } from 'components/RequisitesFormModal/requisitesMapper';

import { selectAmounts } from 'store/autoReducers';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/common/BalanceReplenishmentModal/BalanceReplenishmentModal.module.less';

const TrlKeys = {
    title: 'Dashboard.BalanceReplenishmentModal.title',
    description: {
        part0: 'Dashboard.BalanceReplenishmentModal.description.part0',
        part1: 'Dashboard.BalanceReplenishmentModal.description.part1',
    },
    input: {
        label: 'Dashboard.BalanceReplenishmentModal.input.label',
        placeholder: 'Dashboard.BalanceReplenishmentModal.input.placeholder',
    },
    tip: 'InvoiceCreate.tip',
    tipLink: 'InvoiceCreate.tipLink',
    cancelButton: 'Dashboard.BalanceReplenishmentModal.cancelButton',
    confirmButton: 'Dashboard.BalanceReplenishmentModal.confirmButton',
    currency: 'currency.short',
    minValueError: 'Dashboard.BalanceReplenishmentModal.input.error.minValue',
};

const BalanceReplenishmentModal: FC<{
    isVisible: boolean;
    closeModal: () => void;
    withDescription?: boolean;
}> = ({ isVisible, closeModal, withDescription = true }) => {
    const { minAmount, suggestedAmounts } = useSelector(selectAmounts);
    const [employerName, setEmployerName] = useState<string | null | undefined>('');

    const defaultAmountForReplenishment = useDefaultAmountForReplenishment();

    const [amountForReplenishment, setAmountForReplenishment] = useState(String(defaultAmountForReplenishment));
    const [validationError, setValidationError] = useState('');

    const trls = useTrls();
    const dispatch = useDispatch();
    const { isMobile } = useBreakpoint();

    useEffect(() => {
        void fetcher.get<unknown, RequisitesData>('/api/v1/requisites').then((requisites) => {
            setEmployerName(requisites[RequisitesFormName.FinalAdvertiserRequisites]?.legalName);
        });
    }, [dispatch]);

    const onCloseModal = () => {
        closeModal();
        clickmePaymentModalButtonClick({ buttonName: 'clickme_payment_modal_cancel' });
    };

    const onConfirm = () => {
        if (amountForReplenishment === '' || Number(amountForReplenishment) < minAmount) {
            setValidationError(trls(TrlKeys.minValueError, [formatCost(minAmount, NON_BREAKING_SPACE)]));
            return;
        }
        void dispatch(purchase(String(Number(amountForReplenishment) * CENTS_IN_RUB), closeModal));
        clickmePaymentModalButtonClick({
            buttonName: 'clickme_payment_modal_confirm',
            paymentAmount: Number(amountForReplenishment),
        });
    };

    const content = (
        <ElementShownAnchor fn={clickmePaymentModalElementShown}>
            {withDescription && (
                <>
                    <Text typography="paragraph-2-regular">
                        {trls(TrlKeys.description.part0)}
                        {NON_BREAKING_SPACE}
                        <Text Element="span" typography="subtitle-1-semibold">
                            {trls(TrlKeys.description.part1, [
                                formatCost(defaultAmountForReplenishment, NON_BREAKING_SPACE),
                            ])}
                        </Text>
                    </Text>
                    <VSpacing default={16} />
                    <FormLabel htmlFor="replenishment-amount">{trls(TrlKeys.input.label)}</FormLabel>
                    <VSpacing default={12} />
                </>
            )}
            <VSpacingContainer default={16}>
                <NumberInput
                    id="replenishment-amount"
                    decimalMark={[]}
                    decimalLength={0}
                    onChange={setAmountForReplenishment}
                    onFocus={() => clickmePaymentModalFormFieldTouch()}
                    placeholder={trls(TrlKeys.input.placeholder)}
                    postfix={trls(TrlKeys.currency)}
                    size="medium"
                    value={amountForReplenishment}
                    invalid={!!validationError}
                    errorMessage={validationError}
                />
                <ChipsContainer>
                    {suggestedAmounts.map((amount, index) => (
                        <CustomChip
                            key={index}
                            Element="button"
                            size="small"
                            onClick={() => {
                                setAmountForReplenishment(String(amount));
                                clickmePaymentModalChipButtonClick({ paymentAmount: amount });
                            }}
                        >
                            {formatCost(amount, NON_BREAKING_SPACE)}
                            {NON_BREAKING_SPACE}
                            {trls(TrlKeys.currency)}
                        </CustomChip>
                    ))}
                </ChipsContainer>
                {employerName && (
                    <Banner
                        showClose={false}
                        style="secondary"
                        stretched
                        content={
                            <Text>
                                {formatToReactComponent(trls(TrlKeys.tip), {
                                    '[0]': (
                                        <Link inline target="_blank" href="https://hh.ru/article/about_ads">
                                            {trls(TrlKeys.tipLink)}
                                        </Link>
                                    ),
                                    '[1]': employerName,
                                })}
                            </Text>
                        }
                    />
                )}
            </VSpacingContainer>
        </ElementShownAnchor>
    );

    const buttons = (
        <>
            <Button
                stretched
                data-qa="balance-replenishment-modal-cancel-button"
                mode="secondary"
                style="accent"
                onClick={onCloseModal}
            >
                {trls(TrlKeys.cancelButton)}
            </Button>
            <Button
                stretched
                data-qa="balance-replenishment-modal-confirm-button"
                mode="primary"
                style="accent"
                onClick={onConfirm}
            >
                {trls(TrlKeys.confirmButton)}
            </Button>
        </>
    );

    const footer = (
        <ActionBar
            type="vertical"
            primaryActions={isMobile ? buttons : <div className={styles.desktopButtonsWrapper}>{buttons}</div>}
        />
    );

    return (
        <>
            <Modal
                header={<MagritteModalHeader title={trls(TrlKeys.title)} />}
                footer={footer}
                visible={isVisible}
                onClose={onCloseModal}
                size="small"
                data-qa="balance-replenishment-modal"
            >
                {content}
            </Modal>
            <BottomSheet
                height="content"
                visible={isVisible}
                onClose={onCloseModal}
                header={
                    <NavBar>
                        <NavBar.Pane>
                            <VSpacing default={8} />
                            <NavBar.Actions centered>
                                <NavBar.TitleContainer
                                    title={<Text typography="subtitle-1-semibold">{trls(TrlKeys.title)}</Text>}
                                />
                            </NavBar.Actions>
                        </NavBar.Pane>
                    </NavBar>
                }
                footer={footer}
                data-qa="balance-replenishment-bottom-sheet"
            >
                {content}
            </BottomSheet>
        </>
    );
};

export default withErrorBoundary(BalanceReplenishmentModal);
